import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { useUrlPagination } from '../../lib/usePagination.js';
import {
  PageHeader,
  LoadingSkeleton,
  ErrorState,
  KpiHeroBand,
  BrandedEmptyState,
  StaggerReveal,
  PageViewerControls,
  toast,
  activityRail,
} from '../../components/index.js';
import './me.css';
import './me.redesign.css';

export function SavedViewsPage() {
  const [params, setParams] = useSearchParams();
  const pager = useUrlPagination(params, setParams, { defaultPageSize: 8 });
  const r = useApi('/saved-views', { query: { limit: pager.pageSize, offset: pager.offset } });
  const mut = useMutation();
  const [name, setName] = useState('Mission impact · Q3 readout');
  const [route, setRoute] = useState('/executive');

  async function add(e) {
    e.preventDefault();
    const viewName = name.trim();
    const routePath = route.trim() || '/executive';
    if (!viewName) return;
    try {
      const res = await mut.run(() => api.post('/saved-views', { name: viewName, routePath }));
      const savedView = res.data || {
        id: `SV-${Date.now()}`,
        name: viewName,
        routePath,
        scope: 'self',
      };
      toast.success({ title: 'View saved', body: viewName });
      activityRail.push({
        action: 'saved_view.create',
        targetType: 'saved_view',
        targetId: savedView.id,
        actorUserId: 'you',
      });
      setName('');
      r.setData?.((prev) => {
        const current = prev?.data || [];
        return prev
          ? {
              ...prev,
              data: [savedView, ...current],
              total: typeof prev.total === 'number' ? prev.total + 1 : current.length + 1,
            }
          : prev;
      });
    } catch (e) {
      toast.error({ title: 'Could not save view', body: e.message });
    }
  }

  if (r.loading)
    return (
      <>
        <PageHeader title="Saved views" />
        <LoadingSkeleton rows={5} />
      </>
    );
  if (r.error) return <ErrorState error={r.error} />;
  const items = r.data?.data || [];
  const totalViews =
    typeof r.data?.total === 'number' ? r.data.total : (r.data?.page?.total ?? items.length);
  const sharedCount = items.filter(
    (v) => String(v.scope || 'self').toLowerCase() !== 'self',
  ).length;
  const distinctRoutes = new Set(items.map((v) => v.routePath || v.route).filter(Boolean)).size;

  return (
    <>
      <PageHeader
        eyebrow="Me · Saved views"
        title="Pinned filters and routes"
        subtitle="Quick links into the system with the exact filters you last used."
      />
      <KpiHeroBand
        className="me-profile-kpis"
        eyebrow="Saved view rollup"
        items={[
          {
            id: 'total',
            label: 'Saved views',
            value: totalViews,
            status: totalViews > 0 ? 'blue' : 'slate',
            statusLabel: totalViews > 0 ? 'Pinned' : 'Empty',
            footer: 'Routes you can jump back into.',
          },
          {
            id: 'routes',
            label: 'Distinct routes',
            value: distinctRoutes,
            footer: 'Unique destinations on this page.',
          },
          {
            id: 'shared',
            label: 'Shared scope',
            value: sharedCount,
            status: sharedCount > 0 ? 'sage' : 'slate',
            statusLabel: sharedCount > 0 ? 'Team' : 'Personal',
            footer: sharedCount > 0 ? 'Visible beyond you.' : 'All personal so far.',
          },
        ]}
        columns={3}
      />
      <form
        className="card"
        onSubmit={add}
        style={{ marginBottom: 'var(--space-4)', marginTop: 'var(--space-4)' }}
      >
        <div className="row" style={{ gap: 12 }}>
          <input
            className="audit-filter"
            placeholder="View name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ flex: 1 }}
          />
          <input
            className="audit-filter"
            placeholder="Route (e.g. /executive)"
            value={route}
            onChange={(e) => setRoute(e.target.value)}
            style={{ width: 220 }}
          />
          <button className="btn primary tactile-press" disabled={mut.running || !name.trim()}>
            {mut.running ? 'Saving…' : 'Save view'}
          </button>
        </div>
      </form>
      {items.length === 0 ? (
        <BrandedEmptyState
          icon="★"
          eyebrow="Nothing pinned yet"
          title="No saved views"
          description="Save a view from any route to pin it here — it remembers the exact filters you last applied."
          tone="slate"
        />
      ) : (
        <StaggerReveal className="saved-views-grid">
          {items.map((v) => (
            <Link
              key={v.id}
              to={v.routePath || v.route || '/executive'}
              className="saved-view-card"
            >
              <strong>{v.name}</strong>
              <span className="saved-view-route">{v.routePath || v.route}</span>
              <p className="muted small">
                {v.description || v.summary || `Scope: ${v.scope || 'self'}`}
              </p>
            </Link>
          ))}
        </StaggerReveal>
      )}
      <PageViewerControls
        page={r.data?.page}
        label="saved views"
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </>
  );
}
